import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Input,
} from "@quietbuildlab/ui";
import { categoriesQueryOptions } from "../queries/deals";
import { getRecentSearches, addRecentSearch } from "../lib/recent-searches";

type Props = {
  open: boolean;
  onClose: () => void;
};

export function CommandPalette({ open, onClose }: Props) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const categories = useQuery({ ...categoriesQueryOptions, enabled: open });
  const [recents, setRecents] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setQ("");
      setRecents(getRecentSearches());
    }
  }, [open]);

  // Note: Radix Dialog handles Escape, focus trap, scroll lock, and click-outside.
  // The manual onKey/queueMicrotask/onClick stopPropagation are no longer needed.

  const filteredCats = categories.data?.filter((c) =>
    c.name.toLowerCase().includes(q.toLowerCase()),
  ) ?? [];
  const filteredRecents = recents.filter((r) =>
    r.toLowerCase().includes(q.toLowerCase()),
  );

  function go(term: string) {
    const t = term.trim();
    if (!t) return;
    addRecentSearch(t);
    onClose();
    navigate({ to: "/search", search: { q: t } });
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        // Override the default-centered, padded shadcn Dialog: top-position
        // it like a command palette and let the inner sections own their padding.
        className="top-[10vh] left-1/2 translate-x-[-50%] translate-y-0 sm:max-w-lg gap-0 p-0 bg-[color:var(--color-panel)] border-[color:var(--color-line)]"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            go(q);
          }}
          className="border-b border-[color:var(--color-line)] p-3"
        >
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search deals or jump to a category…"
            className="border-0 bg-transparent px-0 h-auto py-0 shadow-none focus-visible:ring-0 focus-visible:border-transparent"
          />
        </form>
        <div className="max-h-[60vh] overflow-y-auto p-2 text-sm">
          {filteredCats.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[color:var(--color-fg-muted)] px-2 pt-1">
                Categories
              </p>
              <ul>
                {filteredCats.map((c) => (
                  <li key={c.slug}>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        onClose();
                        navigate({ to: "/category/$slug", params: { slug: c.slug } });
                      }}
                      className="w-full justify-start h-auto py-1.5 px-2 text-sm font-normal hover:bg-[color:var(--color-panel-2)]"
                    >
                      {c.name}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {filteredRecents.length > 0 && (
            <div className="mt-2">
              <p className="text-[10px] uppercase tracking-wider text-[color:var(--color-fg-muted)] px-2 pt-1">
                Recent searches
              </p>
              <ul>
                {filteredRecents.map((r) => (
                  <li key={r}>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => go(r)}
                      className="w-full justify-start h-auto py-1.5 px-2 text-sm font-normal hover:bg-[color:var(--color-panel-2)]"
                    >
                      {r}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {filteredCats.length === 0 && filteredRecents.length === 0 && q.trim().length > 0 && (
            <p className="text-[color:var(--color-fg-muted)] px-2 py-2 text-xs">
              Press Enter to search for “{q.trim()}”.
            </p>
          )}
          {filteredCats.length === 0 && filteredRecents.length === 0 && q.trim().length === 0 && (
            <p className="text-[color:var(--color-fg-muted)] px-2 py-2 text-xs">
              Type to search. ↩ for full-text search · Esc to close.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
